import { Component } from 'react';
import type { ReactNode } from 'react';
import Card from './Card';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: { componentStack: string } | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: { componentStack: string }) {
    console.error('React Error Boundary caught an error:', error);
    console.error('Component stack:', errorInfo.componentStack);
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen bg-red-50 flex items-center justify-center p-4">
          <Card className="shadow-lg p-6 max-w-2xl w-full">
            <h1 className="text-2xl font-bold text-red-600 mb-4">
              ⚠️ Fehler in der Anwendung
            </h1>
            <div className="space-y-4">
              <div>
                <h2 className="font-semibold text-gray-900 mb-2">Fehlermeldung:</h2>
                <pre className="bg-red-100 p-3 rounded text-sm overflow-auto">
                  {this.state.error?.toString()}
                </pre>
              </div>
              
              {this.state.error?.stack && (
                <div>
                  <h2 className="font-semibold text-gray-900 mb-2">Stack Trace:</h2>
                  <pre className="bg-gray-100 p-3 rounded text-xs overflow-auto max-h-64">
                    {this.state.error.stack}
                  </pre>
                </div>
              )}

              {this.state.errorInfo?.componentStack && (
                <div>
                  <h2 className="font-semibold text-gray-900 mb-2">Component Stack:</h2>
                  <pre className="bg-gray-100 p-3 rounded text-xs overflow-auto max-h-64">
                    {this.state.errorInfo.componentStack}
                  </pre>
                </div>
              )}

              <button
                onClick={() => window.location.reload()}
                className="btn btn-primary w-full transition"
              >
                Seite neu laden
              </button>
            </div>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
